/**
 * Export card in the Import & Export hub — the inverse of "Import Employees".
 *
 * Downloads the whole employee directory as a CSV in the exact layout the
 * importer reads, so HR can pull the master data out, edit it in a spreadsheet,
 * and load it straight back in (or into the legacy HRIS). No filters: it's a
 * one-click full export. Large directories arrive as a zip of files, each under
 * the import limit.
 */

import { useState } from 'react'
import { Card } from '../../components/primitives/Card'
import { primaryBtn } from '../hr-leave-import/LeaveImportSteps'
import { exportEmployees } from '../../api/hr-employee-export'
import { ApiError } from '../../api/client'

export function EmployeeExportCard(): JSX.Element {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleExport(): Promise<void> {
    setError(null)
    setBusy(true)
    try {
      await exportEmployees()
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Export failed. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card padding={24}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)' }}>
          Export every employee to a CSV. The file uses the same columns as the import, so you can
          edit it and load it straight back in. Large directories come as a zip of files, each under
          the import limit.
        </p>

        {error != null ? (
          <div
            role="alert"
            style={{
              background: 'var(--danger-bg)',
              color: 'var(--danger-fg)',
              border: '1px solid var(--danger-line)',
              padding: '10px 14px',
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        ) : null}

        <div>
          <button
            type="button"
            onClick={handleExport}
            disabled={busy}
            data-testid="export-employees-btn"
            style={primaryBtn(busy)}
          >
            {busy ? 'Preparing…' : 'Export CSV'}
          </button>
        </div>
      </div>
    </Card>
  )
}
